import React, { useEffect, useState, useMemo } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { parseLocalDate, toDateStr } from '../lib/dates'
import { useIsMobile } from '../components/Layout'
import { nextDueDate, daysOverdue, maybeGenerateOnCompletion, generateNextOccurrence, seriesRootId } from '../lib/recurringJobs'

const STATUS_COLOR = { active: '#16a34a', completed: '#2563eb', pending: '#d97706', cancelled: '#dc2626' }
const STATUS_BG = { active: '#dcfce7', completed: '#dbeafe', pending: '#fef3c7', cancelled: '#fef2f2' }
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const CREW_COLORS = ['#2563eb', '#dc2626', '#16a34a', '#9333ea', '#d97706', '#0891b2', '#db2777', '#65a30d', '#4f46e5', '#0d9488']
const GRID_START_HOUR = 6
const GRID_END_HOUR = 20
const HOUR_HEIGHT = 44

// ── pure helpers (no React, easy to reason about independent of the component) ──
function timeToMinutes(t) {
  if (!t) return null
  const [h, m] = t.split(':').map(Number)
  if (Number.isNaN(h)) return null
  return h * 60 + (m || 0)
}
function minutesToTime(mins) {
  mins = Math.max(0, Math.min(23 * 60 + 59, Math.round(mins)))
  const h = Math.floor(mins / 60), m = mins % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}
function formatTimeDisplay(t) {
  const mins = timeToMinutes(t)
  if (mins == null) return ''
  const h = Math.floor(mins / 60), m = mins % 60
  const period = h >= 12 ? 'PM' : 'AM'
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${h12}:${String(m).padStart(2, '0')} ${period}`
}
function formatHourLabel(hour) {
  const period = hour >= 12 ? 'PM' : 'AM'
  const h12 = hour % 12 === 0 ? 12 : hour % 12
  return `${h12} ${period}`
}
function addHoursToTime(startTime, hours) {
  const start = timeToMinutes(startTime)
  if (start == null || !hours) return null
  return minutesToTime(start + hours * 60)
}
function startOfDay(d) { const c = new Date(d); c.setHours(0, 0, 0, 0); return c }
function isMultiDay(job) {
  return !!(job.scheduled_date && job.end_date && job.end_date !== job.scheduled_date)
}
function jobSpansDate(job, date) {
  if (!job.scheduled_date) return false
  const s = startOfDay(parseLocalDate(job.scheduled_date))
  const e = job.end_date ? startOfDay(parseLocalDate(job.end_date)) : s
  const d = startOfDay(date)
  return d >= s && d <= e
}
// Default duration for a job with no end_time -- prefer its own estimated_hours
// (already what drag-to-reschedule uses to auto-set end_time, see addHoursToTime
// call sites) over a flat guess, so a chip's height/conflict window reflects the
// job's real planned length instead of always looking like a 1-hour visit.
function defaultDurationMinutes(job) {
  const hrs = parseFloat(job.estimated_hours)
  return hrs > 0 ? hrs * 60 : 60
}
function jobsOverlap(a, b) {
  if (a.scheduled_date !== b.scheduled_date) return false
  const aStart = timeToMinutes(a.start_time)
  const bStart = timeToMinutes(b.start_time)
  if (aStart == null || bStart == null) return false
  const aEnd = a.end_time ? timeToMinutes(a.end_time) : aStart + defaultDurationMinutes(a)
  const bEnd = b.end_time ? timeToMinutes(b.end_time) : bStart + defaultDurationMinutes(b)
  return aStart < bEnd && bStart < aEnd
}
function computeConflictIds(jobs) {
  const ids = new Set()
  const byDate = {}
  jobs.forEach(j => { if (j.scheduled_date) (byDate[j.scheduled_date] ||= []).push(j) })
  Object.values(byDate).forEach(dayJobs => {
    for (let i = 0; i < dayJobs.length; i++) {
      for (let k = i + 1; k < dayJobs.length; k++) {
        const a = dayJobs[i], b = dayJobs[k]
        const shared = (a.assignments || []).some(x => (b.assignments || []).some(y => y.employee_id === x.employee_id))
        if (shared && jobsOverlap(a, b)) { ids.add(a.id); ids.add(b.id) }
      }
    }
  })
  return ids
}
function matchesSearch(job, query) {
  if (!query) return true
  const q = query.toLowerCase()
  return (job.title || '').toLowerCase().includes(q) || (job.client?.name || '').toLowerCase().includes(q)
}
// Greedy lane assignment so overlapping same-day jobs sit side by side instead of stacking on top of each other.
function layoutDayColumn(dayJobs) {
  const gridMin = GRID_START_HOUR * 60, gridMax = GRID_END_HOUR * 60
  const items = dayJobs.map(j => {
    const rawStart = timeToMinutes(j.start_time)
    const rawEnd = j.end_time ? timeToMinutes(j.end_time) : rawStart + defaultDurationMinutes(j)
    // Clamp both ends into the visible grid -- an unclamped start let a job at/after
    // 8pm compute end < start and render below the bottom of the (clipped) card,
    // effectively invisible while still counting as scheduled.
    const start = Math.min(Math.max(rawStart, gridMin), gridMax - 20)
    const end = Math.min(Math.max(rawEnd, start + 20), gridMax)
    return { job: j, start, end }
  }).sort((a, b) => a.start - b.start)
  const laneEnds = []
  items.forEach(item => {
    let lane = laneEnds.findIndex(end => end <= item.start)
    if (lane === -1) { lane = laneEnds.length; laneEnds.push(item.end) } else laneEnds[lane] = item.end
    item.lane = lane
  })
  const totalLanes = laneEnds.length || 1
  return items.map(item => ({
    job: item.job,
    top: (item.start / 60 - GRID_START_HOUR) * HOUR_HEIGHT,
    height: Math.max(((item.end - item.start) / 60) * HOUR_HEIGHT, 20),
    leftPct: (item.lane / totalLanes) * 100,
    widthPct: 100 / totalLanes,
  }))
}

export default function Schedule() {
  const { isAdmin, profile } = useAuth()
  const isMobileScreen = useIsMobile()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const [tab, setTab] = useState('company') // company | mine | crew | recurring
  const [view, setView] = useState('week') // week | month (company/mine only)
  const [currentDate, setCurrentDate] = useState(new Date())
  const [jobs, setJobs] = useState([])
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedJob, setSelectedJob] = useState(null)
  const [editForm, setEditForm] = useState(null)
  const [savingEdit, setSavingEdit] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [syncMsg, setSyncMsg] = useState('')
  const [filterEmployee, setFilterEmployee] = useState('')
  const [search, setSearch] = useState('')
  const [highlightJobId, setHighlightJobId] = useState(null)
  const [draggedJobId, setDraggedJobId] = useState(null)
  const [undoAction, setUndoAction] = useState(null)

  useEffect(() => { fetchData() }, [])

  // Deep link from Job Detail's "Schedule This Job" button (?job=<id>) — jump to
  // its week and highlight the chip. Clears the param after handling it once, so
  // a later refetch that changes jobs.length (completing a recurring job, etc.)
  // doesn't yank the calendar back here again.
  useEffect(() => {
    const jobParam = searchParams.get('job')
    if (!jobParam || jobs.length === 0) return
    const found = jobs.find(j => j.id === jobParam)
    if (found) {
      if (found.scheduled_date) setCurrentDate(parseLocalDate(found.scheduled_date))
      setHighlightJobId(jobParam)
      setSearchParams(params => { params.delete('job'); return params }, { replace: true })
      const t = setTimeout(() => setHighlightJobId(null), 6000)
      return () => clearTimeout(t)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, jobs.length])

  async function fetchData() {
    setLoading(true)
    const [jobsRes, empRes] = await Promise.all([
      supabase.from('jobs')
        .select('*, client:clients(name), assignments:job_assignments(employee_id, employee:profiles(full_name))')
        .not('status', 'eq', 'cancelled')
        .order('scheduled_date', { ascending: true }),
      supabase.from('profiles').select('id, full_name, active').order('full_name')
    ])
    if (jobsRes.error || empRes.error) {
      alert('Could not load the schedule: ' + (jobsRes.error?.message || empRes.error?.message))
    }
    setJobs(jobsRes.data || [])
    setEmployees(empRes.data || [])
    setLoading(false)
  }

  const conflictIds = useMemo(() => computeConflictIds(jobs), [jobs])
  const employeeColor = (empId) => {
    const idx = employees.findIndex(e => e.id === empId)
    return CREW_COLORS[idx % CREW_COLORS.length] || '#6b7280'
  }

  const visibleJobs = useMemo(() => {
    return jobs.filter(j => {
      if (!matchesSearch(j, search)) return false
      if (tab === 'mine' && !(j.assignments || []).some(a => a.employee_id === profile?.id)) return false
      // Crew tab has its own per-column breakdown -- the flat employee filter is
      // hidden there but the stale state shouldn't silently empty out other columns.
      if (tab !== 'crew' && filterEmployee && !(j.assignments || []).some(a => a.employee_id === filterEmployee)) return false
      return true
    })
  }, [jobs, search, tab, filterEmployee, profile])

  // ── Week navigation ──
  function getWeekStart(date) { const d = new Date(date); d.setDate(d.getDate() - d.getDay()); d.setHours(0, 0, 0, 0); return d }
  function getWeekDays(date) { const start = getWeekStart(date); return Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d }) }
  function getMonthDays(date) {
    const year = date.getFullYear(), month = date.getMonth()
    const firstDay = new Date(year, month, 1), lastDay = new Date(year, month + 1, 0)
    const days = []
    for (let i = 0; i < firstDay.getDay(); i++) { const d = new Date(firstDay); d.setDate(firstDay.getDate() - (firstDay.getDay() - i)); days.push({ date: d, currentMonth: false }) }
    for (let d = 1; d <= lastDay.getDate(); d++) days.push({ date: new Date(year, month, d), currentMonth: true })
    while (days.length % 7 !== 0) { const last = days[days.length - 1].date; const d = new Date(last); d.setDate(last.getDate() + 1); days.push({ date: d, currentMonth: false }) }
    return days
  }
  function isSameDay(d1, d2) { return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate() }
  function navigateRange(dir) {
    const d = new Date(currentDate)
    if (isMobileScreen) d.setDate(d.getDate() + dir)
    else if (view === 'week') d.setDate(d.getDate() + dir * 7)
    else d.setMonth(d.getMonth() + dir)
    setCurrentDate(d)
  }
  function headerLabel() {
    if (isMobileScreen) return currentDate.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })
    if (view === 'week') {
      const days = getWeekDays(currentDate); const start = days[0], end = days[6]
      if (start.getMonth() === end.getMonth()) return `${MONTHS[start.getMonth()]} ${start.getDate()} – ${end.getDate()}, ${start.getFullYear()}`
      return `${MONTHS[start.getMonth()]} ${start.getDate()} – ${MONTHS[end.getMonth()]} ${end.getDate()}, ${end.getFullYear()}`
    }
    return `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`
  }

  // ── Reschedule / reassign mutations, with undo ──
  async function applyReschedule(job, newDate, newStartTime) {
    const prev = { scheduled_date: job.scheduled_date, start_time: job.start_time, end_time: job.end_time, end_date: job.end_date }
    let newEndTime = job.end_time
    if (newStartTime && newStartTime !== job.start_time) {
      if (job.end_time && job.start_time) {
        const dur = timeToMinutes(job.end_time) - timeToMinutes(job.start_time)
        newEndTime = dur > 0 ? minutesToTime(timeToMinutes(newStartTime) + dur) : job.end_time
      } else if (job.estimated_hours) {
        newEndTime = addHoursToTime(newStartTime, job.estimated_hours)
      }
    }
    // Multi-day jobs must keep their span when dragged -- shift end_date by the
    // same number of days scheduled_date moved, or the job vanishes from every
    // calendar view the moment end_date falls before the new scheduled_date.
    let newEndDate = job.end_date
    if (job.end_date && job.end_date !== job.scheduled_date && job.scheduled_date) {
      const deltaDays = Math.round((parseLocalDate(newDate) - parseLocalDate(job.scheduled_date)) / 86400000)
      const shiftedEnd = parseLocalDate(job.end_date)
      shiftedEnd.setDate(shiftedEnd.getDate() + deltaDays)
      newEndDate = toDateStr(shiftedEnd)
    }
    const updates = { scheduled_date: newDate, start_time: newStartTime || null, end_time: newEndTime || null, end_date: newEndDate || null }
    setJobs(prevJobs => prevJobs.map(j => j.id === job.id ? { ...j, ...updates } : j))
    const { error } = await supabase.from('jobs').update(updates).eq('id', job.id)
    if (error) {
      setJobs(prevJobs => prevJobs.map(j => j.id === job.id ? { ...j, ...prev } : j))
      alert(`Could not move "${job.title}": ` + error.message)
      return
    }
    const label = `Moved "${job.title}" to ${parseLocalDate(newDate).toLocaleDateString()}${newStartTime ? ' ' + formatTimeDisplay(newStartTime) : ''}`
    setUndoAction({ jobId: job.id, prev, label })
    setTimeout(() => setUndoAction(cur => (cur && cur.jobId === job.id ? null : cur)), 6000)
  }
  async function undoLastMove() {
    if (!undoAction) return
    const { jobId, prev } = undoAction
    const beforeUndo = jobs.find(j => j.id === jobId)
    setJobs(prevJobs => prevJobs.map(j => (j.id === jobId ? { ...j, ...prev } : j)))
    const { error } = await supabase.from('jobs').update(prev).eq('id', jobId)
    if (error) {
      if (beforeUndo) setJobs(prevJobs => prevJobs.map(j => (j.id === jobId ? beforeUndo : j)))
      alert('Undo failed: ' + error.message)
      return
    }
    setUndoAction(null)
  }
  async function reassignJob(jobId, newEmployeeId) {
    const job = jobs.find(j => j.id === jobId)
    if (!job) return
    const { error: delError } = await supabase.from('job_assignments').delete().eq('job_id', jobId)
    if (delError) { alert('Reassign failed: ' + delError.message); return }
    if (newEmployeeId) {
      const { error: insError } = await supabase.from('job_assignments').insert([{ job_id: jobId, employee_id: newEmployeeId }])
      if (insError) { alert('Reassign failed: ' + insError.message); fetchData(); return }
    }
    fetchData()
  }
  function handleDragStart(e, job) {
    setDraggedJobId(job.id)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', job.id)
  }
  function getDraggedJob(e) {
    const id = draggedJobId || e.dataTransfer.getData('text/plain')
    return jobs.find(j => j.id === id)
  }
  function handleSlotDrop(e, day, hour) {
    e.preventDefault()
    const job = getDraggedJob(e)
    setDraggedJobId(null)
    if (!job) return
    let startTime = job.start_time
    if (hour != null) {
      const rect = e.currentTarget.getBoundingClientRect()
      const offsetY = e.clientY - rect.top
      const minuteOffset = Math.round((offsetY / HOUR_HEIGHT) * 60 / 15) * 15
      startTime = minutesToTime(hour * 60 + minuteOffset)
    }
    applyReschedule(job, toDateStr(day), startTime)
  }
  function handleMonthDrop(e, day) {
    e.preventDefault()
    const job = getDraggedJob(e)
    setDraggedJobId(null)
    if (!job) return
    applyReschedule(job, toDateStr(day), job.start_time)
  }
  function handleCrewDrop(e, employeeId) {
    e.preventDefault()
    const job = getDraggedJob(e)
    setDraggedJobId(null)
    if (!job) return
    reassignJob(job.id, employeeId)
  }
  function handleSlotClick(day, hour) {
    const d = toDateStr(day)
    const t = hour != null ? `${String(hour).padStart(2, '0')}:00` : ''
    navigate(`/jobs/new?date=${d}${t ? `&time=${t}` : ''}`)
  }

  async function quickAdvanceStatus(job, e) {
    e.stopPropagation()
    const order = ['pending', 'active', 'completed']
    const idx = order.indexOf(job.status)
    // Advance-only, never wraps back to 'pending' -- reaching 'completed' fires
    // recurring generation, so a wrap let two accidental taps un-complete a job
    // (leaving completed_at stale) and a third tap mint a duplicate next visit.
    const nextStatus = order[Math.min(idx + 1, order.length - 1)]
    if (nextStatus === job.status) return
    const wasCompleted = job.status === 'completed'
    const updates = { status: nextStatus }
    if (nextStatus === 'completed') updates.completed_at = new Date().toISOString()
    setJobs(prev => prev.map(j => (j.id === job.id ? { ...j, ...updates } : j)))
    const { error } = await supabase.from('jobs').update(updates).eq('id', job.id)
    if (error) {
      setJobs(prev => prev.map(j => (j.id === job.id ? { ...j, status: job.status, completed_at: job.completed_at } : j)))
      alert('Status update failed: ' + error.message)
      return
    }
    const { recurrenceError } = await maybeGenerateOnCompletion(supabase, job, nextStatus, wasCompleted, { createdBy: profile?.id })
    if (recurrenceError) alert('Marked complete, but the next recurring visit could not be created: ' + recurrenceError.message)
    else if (nextStatus === 'completed' && !wasCompleted && job.is_recurring) fetchData()
  }

  // ── Popup: fully editable ──
  useEffect(() => {
    if (selectedJob) {
      setEditForm({
        status: selectedJob.status,
        start_time: selectedJob.start_time || '',
        end_time: selectedJob.end_time || '',
        notes: selectedJob.notes || '',
        assigned: (selectedJob.assignments || []).map(a => a.employee_id),
      })
    } else setEditForm(null)
  }, [selectedJob])

  async function savePopupEdit() {
    if (!selectedJob || !editForm) return
    setSavingEdit(true)
    const wasCompleted = selectedJob.status === 'completed'
    const updates = {
      status: editForm.status,
      start_time: editForm.start_time || null,
      end_time: editForm.end_time || null,
      notes: editForm.notes || null,
    }
    if (editForm.status === 'completed' && !wasCompleted) updates.completed_at = new Date().toISOString()
    const { error } = await supabase.from('jobs').update(updates).eq('id', selectedJob.id)
    if (error) { setSavingEdit(false); alert('Save failed: ' + error.message); return }
    if (isAdmin) {
      const before = (selectedJob.assignments || []).map(a => a.employee_id)
      const toAdd = editForm.assigned.filter(id => !before.includes(id))
      const toRemove = before.filter(id => !editForm.assigned.includes(id))
      const results = await Promise.all([
        ...toAdd.map(employee_id => supabase.from('job_assignments').insert([{ job_id: selectedJob.id, employee_id }])),
        ...toRemove.map(employee_id => supabase.from('job_assignments').delete().eq('job_id', selectedJob.id).eq('employee_id', employee_id)),
      ])
      const assignError = results.find(r => r.error)
      if (assignError) alert('Job saved, but crew assignment failed: ' + assignError.error.message)
    }
    const { recurrenceError } = await maybeGenerateOnCompletion(supabase, selectedJob, editForm.status, wasCompleted, { createdBy: profile?.id })
    if (recurrenceError) alert('Saved, but the next recurring visit could not be created: ' + recurrenceError.message)
    setSavingEdit(false)
    setSelectedJob(null)
    fetchData()
  }

  async function syncToGoogleCalendar(job) {
    setSyncing(true)
    setSyncMsg('')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const startDate = job.scheduled_date ? job.scheduled_date.split('T')[0] : toDateStr(new Date())
      const title = `TLC: ${job.title}${job.client?.name ? ` — ${job.client.name}` : ''}`
      const description = [
        job.description || '',
        job.address ? `📍 ${job.address}` : '',
        job.contract_amount ? `💰 $${job.contract_amount}` : '',
        job.crew_size ? `👷 Crew: ${job.crew_size}` : '',
        `Status: ${job.status}`,
      ].filter(Boolean).join('\n')
      const response = await fetch('https://calendarmcp.googleapis.com/mcp/v1', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({ method: 'calendar.events.insert', params: { calendarId: 'primary', event: { summary: title, description, start: { date: startDate }, end: { date: startDate } } } })
      })
      setSyncMsg(response.ok ? '✅ Added to Google Calendar' : '⚠️ Could not sync — check Google Calendar connection')
    } catch (e) { setSyncMsg('⚠️ Sync failed: ' + e.message) }
    setSyncing(false)
    setTimeout(() => setSyncMsg(''), 4000)
  }

  async function skipOccurrence(job) {
    if (!job.scheduled_date) {
      alert('This recurring job has no date set — schedule it first before skipping an occurrence.')
      return
    }
    // Generate the replacement FIRST, cancel second. Cancelled jobs are excluded
    // from every fetch here, so cancelling first and having generation fail
    // would leave the series with no live row and no way back.
    let next
    try {
      next = await generateNextOccurrence(supabase, job, { createdBy: profile?.id })
    } catch (e) {
      alert('Could not generate the next occurrence — nothing was skipped: ' + e.message)
      return
    }
    if (!next) {
      alert('Could not generate the next occurrence — nothing was skipped.')
      return
    }
    const { error } = await supabase.from('jobs').update({ status: 'cancelled' }).eq('id', job.id)
    if (error) alert('Generated the next visit, but could not mark this occurrence skipped: ' + error.message)
    fetchData()
  }

  if (loading) return <div style={styles.loading}>Loading schedule...</div>

  const weekDays = getWeekDays(currentDate)
  const monthDays = getMonthDays(currentDate)
  const today = new Date()
  const hours = Array.from({ length: GRID_END_HOUR - GRID_START_HOUR }, (_, i) => GRID_START_HOUR + i)
  const unscheduled = visibleJobs.filter(j => !j.scheduled_date && j.status !== 'completed' && j.status !== 'cancelled')

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>{isAdmin ? 'Schedule' : 'My Schedule'}</h1>
          <p style={styles.sub}>Dispatch & calendar view — drag a job to reschedule</p>
        </div>
        <div style={styles.headerActions}>
          {tab !== 'recurring' && (
            <input style={styles.searchInput} placeholder="Search client or job..." value={search} onChange={e => setSearch(e.target.value)} />
          )}
          {isAdmin && tab !== 'recurring' && tab !== 'crew' && (
            <select style={styles.filterSelect} value={filterEmployee} onChange={e => setFilterEmployee(e.target.value)}>
              <option value="">All Crew</option>
              {employees.map(e => <option key={e.id} value={e.id}>{e.full_name}</option>)}
            </select>
          )}
          {(tab === 'company' || tab === 'mine') && !isMobileScreen && (
            <div style={styles.viewToggle}>
              <button style={{ ...styles.viewBtn, ...(view === 'week' ? styles.viewBtnActive : {}) }} onClick={() => setView('week')}>Week</button>
              <button style={{ ...styles.viewBtn, ...(view === 'month' ? styles.viewBtnActive : {}) }} onClick={() => setView('month')}>Month</button>
            </div>
          )}
          <Link to="/jobs/new" style={styles.newJobBtn}>+ New Job</Link>
        </div>
      </div>

      {isAdmin && (
        <div style={styles.tabRow}>
          {[['company', 'Company'], ['mine', 'My Jobs'], ['crew', 'Crew'], ['recurring', 'Recurring']].map(([key, label]) => (
            <button key={key} style={{ ...styles.tabBtn, ...(tab === key ? styles.tabBtnActive : {}) }} onClick={() => setTab(key)}>{label}</button>
          ))}
        </div>
      )}

      {tab !== 'recurring' && (
        <div style={styles.calNav}>
          <button style={styles.navBtn} onClick={() => navigateRange(-1)}>‹</button>
          <span style={styles.calLabel}>{headerLabel()}</span>
          <button style={styles.navBtn} onClick={() => navigateRange(1)}>›</button>
          <button style={styles.todayBtn} onClick={() => setCurrentDate(new Date())}>Today</button>
        </div>
      )}

      {syncMsg && <div style={styles.syncMsg}>{syncMsg}</div>}
      {undoAction && (
        <div style={styles.undoToast}>
          <span>{undoAction.label}</span>
          <button style={styles.undoBtn} onClick={undoLastMove}>Undo</button>
        </div>
      )}

      {tab === 'recurring' ? (
        <RecurringList jobs={jobs} onSkip={skipOccurrence} onOpenJob={setSelectedJob} />
      ) : tab === 'crew' ? (
        <CrewBoard
          jobs={visibleJobs.filter(j => jobSpansDate(j, currentDate))}
          employees={employees}
          employeeColor={employeeColor}
          conflictIds={conflictIds}
          onDrop={handleCrewDrop}
          onDragStart={handleDragStart}
          onOpenJob={setSelectedJob}
        />
      ) : isMobileScreen ? (
        <AgendaView
          jobs={visibleJobs.filter(j => jobSpansDate(j, currentDate))}
          conflictIds={conflictIds}
          onOpenJob={setSelectedJob}
          onQuickStatus={quickAdvanceStatus}
        />
      ) : view === 'week' ? (
        <div style={styles.calCard}>
          {/* Day header row */}
          <div style={styles.weekHeaderGrid}>
            <div style={styles.gutterCell} />
            {weekDays.map((day, i) => {
              const isToday = isSameDay(day, today)
              return (
                <div key={i} style={{ ...styles.dayHeader, ...(isToday ? styles.dayHeaderToday : {}) }}>
                  <span style={styles.dayName}>{DAYS[i]}</span>
                  <span style={{ ...styles.dayNum, ...(isToday ? styles.dayNumToday : {}) }}>{day.getDate()}</span>
                </div>
              )
            })}
          </div>
          {/* Multi-day + untimed row — a single grid row, so every column (and the gutter)
              auto-normalizes to the same height no matter how many untimed jobs a given day has.
              That's what keeps the hour labels below correctly aligned with the hour grid. */}
          <div style={styles.weekBannerGrid}>
            <div style={styles.gutterLabelSmall}>All-day</div>
            {weekDays.map((day, i) => {
              const dateKey = toDateStr(day)
              const multiDay = visibleJobs.filter(j => isMultiDay(j) && jobSpansDate(j, day))
              const untimed = visibleJobs.filter(j => j.scheduled_date === dateKey && !isMultiDay(j) && !j.start_time)
              return (
                <div key={i} style={styles.bannerCell} onDragOver={e => e.preventDefault()} onDrop={e => handleMonthDrop(e, day)}>
                  {multiDay.map(job => (
                    <div key={job.id} draggable onDragStart={e => handleDragStart(e, job)} onClick={() => setSelectedJob(job)}
                      style={{ ...styles.bannerChip, background: STATUS_COLOR[job.status] || '#6b7280', ...(highlightJobId === job.id ? styles.highlighted : {}), ...(conflictIds.has(job.id) ? styles.conflicted : {}) }}>
                      {job.title}
                    </div>
                  ))}
                  {untimed.map(job => (
                    <button key={job.id} draggable onDragStart={e => handleDragStart(e, job)} onClick={() => setSelectedJob(job)}
                      style={{ ...styles.jobChip, background: STATUS_BG[job.status] || '#f3f4f6', borderLeft: `3px solid ${STATUS_COLOR[job.status] || '#6b7280'}`, ...(highlightJobId === job.id ? styles.highlighted : {}), ...(conflictIds.has(job.id) ? styles.conflicted : {}) }}>
                      <span style={styles.chipTitle}>{job.title}</span>
                    </button>
                  ))}
                </div>
              )
            })}
          </div>
          {/* Hour grid row */}
          <div style={styles.weekHourOuterGrid}>
            <div style={{ ...styles.hourLabelCol, height: (GRID_END_HOUR - GRID_START_HOUR) * HOUR_HEIGHT }}>
              {hours.map(hour => (
                <div key={hour} style={{ ...styles.hourLabel, top: (hour - GRID_START_HOUR) * HOUR_HEIGHT - 7 }}>{formatHourLabel(hour)}</div>
              ))}
            </div>
            {weekDays.map((day, i) => {
              const isToday = isSameDay(day, today)
              const timed = visibleJobs.filter(j => j.scheduled_date === toDateStr(day) && !isMultiDay(j) && j.start_time)
              const positioned = layoutDayColumn(timed)
              return (
                <div key={i} style={{ ...styles.hourGrid, height: (GRID_END_HOUR - GRID_START_HOUR) * HOUR_HEIGHT, ...(isToday ? styles.dayColToday : {}) }}>
                  {hours.map(hour => (
                    <div key={hour} style={{ ...styles.hourCell, top: (hour - GRID_START_HOUR) * HOUR_HEIGHT, height: HOUR_HEIGHT }}
                      onDragOver={e => e.preventDefault()} onDrop={e => handleSlotDrop(e, day, hour)} onClick={() => handleSlotClick(day, hour)} />
                  ))}
                  {positioned.map(({ job, top, height, leftPct, widthPct }) => (
                    <button key={job.id} draggable onDragStart={e => handleDragStart(e, job)}
                      onClick={e => { e.stopPropagation(); setSelectedJob(job) }}
                      style={{
                        ...styles.timedChip, top, height, left: `calc(${leftPct}% + 2px)`, width: `calc(${widthPct}% - 4px)`,
                        background: STATUS_BG[job.status] || '#f3f4f6', borderLeft: `3px solid ${STATUS_COLOR[job.status] || '#6b7280'}`,
                        ...(highlightJobId === job.id ? styles.highlighted : {}), ...(conflictIds.has(job.id) ? styles.conflicted : {}),
                      }}>
                      <span style={styles.chipTitle}>{job.title}</span>
                      <span style={styles.chipTime}>{formatTimeDisplay(job.start_time)}</span>
                      <button title="Advance status" onClick={e => quickAdvanceStatus(job, e)} style={styles.quickStatusBtn}>✓</button>
                    </button>
                  ))}
                </div>
              )
            })}
          </div>
        </div>
      ) : (
        <div style={styles.calCard}>
          <div style={styles.monthGrid}>
            {DAYS.map(d => <div key={d} style={styles.monthDayLabel}>{d}</div>)}
            {monthDays.map(({ date, currentMonth }, i) => {
              const isToday = isSameDay(date, today)
              const dayJobs = visibleJobs.filter(j => jobSpansDate(j, date))
              return (
                <div key={i} style={{ ...styles.monthCell, ...(isToday ? styles.monthCellToday : {}), ...(!currentMonth ? styles.monthCellOther : {}) }}
                  onDragOver={e => e.preventDefault()} onDrop={e => handleMonthDrop(e, date)} onClick={() => dayJobs.length === 0 && handleSlotClick(date)}>
                  <span style={{ ...styles.monthDate, ...(isToday ? styles.monthDateToday : {}) }}>{date.getDate()}</span>
                  <div style={styles.monthJobs}>
                    {dayJobs.slice(0, 3).map(job => (
                      <button key={job.id} draggable onDragStart={e => handleDragStart(e, job)} onClick={e => { e.stopPropagation(); setSelectedJob(job) }}
                        style={{ ...styles.monthChip, background: STATUS_COLOR[job.status] || '#6b7280', ...(highlightJobId === job.id ? { outline: '2px solid #fbbf24' } : {}), ...(conflictIds.has(job.id) ? { outline: '2px solid #dc2626' } : {}) }}>
                        {job.title}
                      </button>
                    ))}
                    {dayJobs.length > 3 && <span style={styles.moreChip}>+{dayJobs.length - 3} more</span>}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {selectedJob && editForm && (
        <div style={styles.popupOverlay} onClick={() => setSelectedJob(null)}>
          <div style={styles.popup} onClick={e => e.stopPropagation()}>
            <div style={styles.popupHeader}>
              <div>
                <h3 style={styles.popupTitle}>{selectedJob.title}</h3>
                {selectedJob.client?.name && <span style={styles.popupClientName}>{selectedJob.client.name}</span>}
              </div>
              <button style={styles.closeBtn} onClick={() => setSelectedJob(null)}>✕</button>
            </div>
            {conflictIds.has(selectedJob.id) && <div style={styles.conflictWarning}>⚠️ Overlaps with another job for the same crew member</div>}
            <div style={styles.popupBody}>
              <div style={styles.popupRow2}>
                <div style={{ flex: 1 }}>
                  <label style={styles.popupLabel}>Status</label>
                  <select style={styles.popupInput} value={editForm.status} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}>
                    <option value="pending">Pending</option><option value="active">Active</option>
                    <option value="completed">Completed</option>
                    {/* Cancel is an admin-only call -- employees get complete/reschedule/notes, not cancel. */}
                    {isAdmin && <option value="cancelled">Cancelled</option>}
                  </select>
                </div>
              </div>
              <div style={styles.popupRow2}>
                <div style={{ flex: 1 }}>
                  <label style={styles.popupLabel}>Start Time</label>
                  <input type="time" style={styles.popupInput} value={editForm.start_time} onChange={e => setEditForm(f => ({ ...f, start_time: e.target.value }))} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={styles.popupLabel}>End Time</label>
                  <input type="time" style={styles.popupInput} value={editForm.end_time} onChange={e => setEditForm(f => ({ ...f, end_time: e.target.value }))} />
                </div>
              </div>
              {selectedJob.address && <div style={styles.popupRow}><span style={styles.popupSmallLabel}>Address</span><span>{selectedJob.address}</span></div>}
              {isAdmin && selectedJob.contract_amount && <div style={styles.popupRow}><span style={styles.popupSmallLabel}>Amount</span><span style={{ fontWeight: '700', color: '#16a34a' }}>${selectedJob.contract_amount.toLocaleString()}</span></div>}
              {isAdmin && (
                <div>
                  <label style={styles.popupLabel}>Assigned Crew</label>
                  <div style={styles.crewCheckList}>
                    {employees.filter(emp => emp.active !== false || editForm.assigned.includes(emp.id)).map(emp => (
                      <label key={emp.id} style={styles.crewCheckItem}>
                        <input type="checkbox" checked={editForm.assigned.includes(emp.id)}
                          onChange={e => setEditForm(f => ({ ...f, assigned: e.target.checked ? [...f.assigned, emp.id] : f.assigned.filter(id => id !== emp.id) }))} />
                        {emp.full_name}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <label style={styles.popupLabel}>Notes</label>
                <textarea style={{ ...styles.popupInput, minHeight: '70px', resize: 'vertical' }} value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} placeholder="Crew notes, access instructions, anything worth flagging..." />
              </div>
            </div>
            <div style={styles.popupFooter}>
              {isAdmin && (
                <button style={styles.syncBtn} onClick={() => syncToGoogleCalendar(selectedJob)} disabled={syncing}>{syncing ? '⏳' : '📅 Google Calendar'}</button>
              )}
              <Link to={`/jobs/${selectedJob.id}`} style={styles.viewJobBtn}>View Full Job</Link>
              <button style={styles.saveBtn} onClick={savePopupEdit} disabled={savingEdit}>{savingEdit ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {unscheduled.length > 0 && tab !== 'recurring' && tab !== 'crew' && (
        <div style={styles.unscheduledSection}>
          <h3 style={styles.unscheduledTitle}>⚠️ Unscheduled Jobs ({unscheduled.length}) — drag onto the calendar to schedule</h3>
          <div style={styles.unscheduledList}>
            {unscheduled.map(job => (
              <div key={job.id} draggable onDragStart={e => handleDragStart(e, job)} onClick={() => setSelectedJob(job)}
                style={{ ...styles.unscheduledCard, ...(highlightJobId === job.id ? styles.highlighted : {}) }}>
                <span style={styles.unscheduledName}>{job.title}</span>
                {job.client?.name && <span style={styles.unscheduledClient}>{job.client.name}</span>}
                <span style={{ ...styles.unscheduledStatus, background: STATUS_BG[job.status], color: STATUS_COLOR[job.status] }}>{job.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Recurring Jobs list ──
function RecurringList({ jobs, onSkip, onOpenJob }) {
  const isMobileScreen = useIsMobile()
  const recurring = jobs.filter(j => j.is_recurring)
  // Group by the actual recurrence chain (walking recurrence_parent_id to its root),
  // not a client+type heuristic -- a client can have two distinct series of the same
  // type (front yard weekly + back yard weekly), which a heuristic key would still
  // collapse into one row and silently hide.
  const jobsById = new Map(jobs.map(j => [j.id, j]))
  const bySeries = {}
  recurring.forEach(j => {
    const key = seriesRootId(j, jobsById)
    if (!bySeries[key] || (j.scheduled_date || '') > (bySeries[key].scheduled_date || '')) bySeries[key] = j
  })
  const rows = Object.values(bySeries).map(job => ({ job, due: nextDueDate(job), overdue: daysOverdue(job) })).sort((a, b) => b.overdue - a.overdue)

  if (rows.length === 0) return <div style={styles.calCard}><div style={{ padding: '30px', textAlign: 'center', color: '#6b7c6b' }}>No recurring jobs yet.</div></div>

  if (isMobileScreen) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {rows.map(({ job, due, overdue }) => (
          <div key={job.id} style={styles.recurringCard} onClick={() => onOpenJob(job)}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <strong>{job.client?.name || job.title}</strong>
              {overdue > 0 && <span style={styles.overdueBadge}>{overdue}d overdue</span>}
            </div>
            <div style={{ fontSize: '13px', color: '#6b7c6b' }}>{job.title} · every {job.recurrence_frequency}</div>
            <div style={{ fontSize: '13px', marginTop: '4px' }}>Next due: {due ? due.toLocaleDateString() : '—'}</div>
            {job.status !== 'completed' && (
              <button style={styles.skipBtn} onClick={e => { e.stopPropagation(); onSkip(job) }}>Skip This Occurrence</button>
            )}
          </div>
        ))}
      </div>
    )
  }
  return (
    <div style={styles.calCard}>
      <table style={styles.recurringTable}>
        <thead><tr>
          <th style={styles.th}>Client</th><th style={styles.th}>Job</th><th style={styles.th}>Cadence</th>
          <th style={styles.th}>Next Due</th><th style={styles.th}>Status</th><th style={styles.th}></th>
        </tr></thead>
        <tbody>
          {rows.map(({ job, due, overdue }) => (
            <tr key={job.id} style={styles.tr}>
              <td style={styles.td} onClick={() => onOpenJob(job)}>{job.client?.name || '—'}</td>
              <td style={styles.td} onClick={() => onOpenJob(job)}>{job.title}</td>
              <td style={styles.td}>{job.recurrence_frequency}</td>
              <td style={styles.td}>
                {due ? due.toLocaleDateString() : '—'}
                {overdue > 0 && <span style={styles.overdueBadge}>{overdue}d overdue</span>}
              </td>
              <td style={styles.td}><span style={{ ...styles.popupClientName, background: STATUS_BG[job.status], color: STATUS_COLOR[job.status], padding: '2px 8px', borderRadius: '10px' }}>{job.status}</span></td>
              <td style={styles.td}>
                {job.status !== 'completed' && <button style={styles.skipBtn} onClick={() => onSkip(job)}>Skip This Occurrence</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Crew board: drag jobs between employee columns to reassign ──
function CrewBoard({ jobs, employees, employeeColor, conflictIds, onDrop, onDragStart, onOpenJob }) {
  // Keep a column for an inactive employee only if they still have a job on the board --
  // otherwise a departed employee accumulates a permanent empty column forever.
  const assignedIds = new Set(jobs.flatMap(j => (j.assignments || []).map(a => a.employee_id)))
  const boardEmployees = employees.filter(e => e.active !== false || assignedIds.has(e.id))
  const columns = [{ id: null, full_name: 'Unassigned' }, ...boardEmployees]
  return (
    <div style={styles.crewBoard}>
      {columns.map(col => {
        const colJobs = jobs.filter(j => col.id === null ? (j.assignments || []).length === 0 : (j.assignments || []).some(a => a.employee_id === col.id))
        return (
          <div key={col.id || 'unassigned'} style={styles.crewColumn} onDragOver={e => e.preventDefault()} onDrop={e => onDrop(e, col.id)}>
            <div style={{ ...styles.crewColumnHeader, borderTop: col.id ? `3px solid ${employeeColor(col.id)}` : '3px solid #9ca3af' }}>{col.full_name}</div>
            {colJobs.map(job => (
              <div key={job.id} draggable onDragStart={e => onDragStart(e, job)} onClick={() => onOpenJob(job)}
                style={{ ...styles.crewJobCard, borderLeft: `3px solid ${STATUS_COLOR[job.status] || '#6b7280'}`, ...(conflictIds.has(job.id) ? styles.conflicted : {}) }}>
                <div style={{ fontWeight: 700, fontSize: '13px' }}>{job.title}</div>
                {job.client?.name && <div style={{ fontSize: '12px', color: '#6b7c6b' }}>{job.client.name}</div>}
                {job.start_time && <div style={{ fontSize: '11px', color: '#9ca3af' }}>{formatTimeDisplay(job.start_time)}</div>}
              </div>
            ))}
            {colJobs.length === 0 && <div style={styles.crewEmpty}>Drop a job here</div>}
          </div>
        )
      })}
    </div>
  )
}

// ── Mobile agenda: vertical, single-day, no drag (touch DnD is unreliable — the popup's time fields are the mobile equivalent) ──
function AgendaView({ jobs, conflictIds, onOpenJob, onQuickStatus }) {
  const sorted = [...jobs].sort((a, b) => (a.start_time || '99:99').localeCompare(b.start_time || '99:99'))
  if (sorted.length === 0) return <div style={styles.calCard}><div style={{ padding: '30px', textAlign: 'center', color: '#6b7c6b' }}>No jobs today.</div></div>
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {sorted.map(job => (
        <div key={job.id} onClick={() => onOpenJob(job)}
          style={{ ...styles.agendaCard, borderLeft: `4px solid ${STATUS_COLOR[job.status] || '#6b7280'}`, ...(conflictIds.has(job.id) ? styles.conflicted : {}) }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', fontWeight: '700', color: '#6b7c6b' }}>{job.start_time ? formatTimeDisplay(job.start_time) : 'No time set'}</span>
            <button onClick={e => onQuickStatus(job, e)} style={styles.quickStatusBtnAgenda}>✓ {job.status}</button>
          </div>
          <div style={{ fontWeight: 700, fontSize: '15px', marginTop: '4px' }}>{job.title}</div>
          {job.client?.name && <div style={{ fontSize: '13px', color: '#6b7c6b' }}>{job.client.name}</div>}
          {job.address && <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>📍 {job.address}</div>}
        </div>
      ))}
    </div>
  )
}

const styles = {
  page: { width: '100%' },
  loading: { padding: '40px', textAlign: 'center', color: '#6b7c6b' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px', flexWrap: 'wrap', gap: '12px' },
  title: { margin: 0, fontSize: '26px', fontWeight: '700', color: '#1a2e1a' },
  sub: { margin: '4px 0 0', color: '#6b7c6b', fontSize: '13px' },
  headerActions: { display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' },
  searchInput: { padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '13px', fontFamily: 'inherit', width: '160px' },
  filterSelect: { padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '13px', fontFamily: 'inherit', background: 'white' },
  viewToggle: { display: 'flex', background: '#f3f4f6', borderRadius: '8px', padding: '3px', gap: '2px' },
  viewBtn: { padding: '6px 14px', borderRadius: '6px', border: 'none', background: 'transparent', fontSize: '13px', fontWeight: '600', cursor: 'pointer', color: '#6b7c6b' },
  viewBtnActive: { background: 'white', color: '#1a2e1a', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' },
  newJobBtn: { background: '#2d4a2d', color: 'white', padding: '8px 16px', borderRadius: '8px', textDecoration: 'none', fontSize: '13px', fontWeight: '600' },
  tabRow: { display: 'flex', gap: '6px', marginBottom: '14px', borderBottom: '1px solid #e5e7eb', paddingBottom: '10px' },
  tabBtn: { padding: '7px 16px', borderRadius: '8px', border: 'none', background: '#f3f4f6', fontSize: '13px', fontWeight: '600', cursor: 'pointer', color: '#6b7c6b' },
  tabBtnActive: { background: '#2d4a2d', color: 'white' },
  calNav: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' },
  navBtn: { background: 'white', border: '1.5px solid #e5e7eb', color: '#374151', width: '34px', height: '34px', borderRadius: '8px', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  calLabel: { fontWeight: '700', fontSize: '16px', color: '#1a2e1a', flex: 1 },
  todayBtn: { background: '#f3f4f6', border: '1.5px solid #e5e7eb', color: '#374151', padding: '6px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  syncMsg: { background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#16a34a', padding: '10px 16px', borderRadius: '8px', fontSize: '13px', marginBottom: '12px' },
  undoToast: { position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)', background: '#1a2e1a', color: 'white', padding: '10px 16px', borderRadius: '10px', fontSize: '13px', display: 'flex', gap: '14px', alignItems: 'center', zIndex: 300, boxShadow: '0 8px 24px rgba(0,0,0,0.25)' },
  undoBtn: { background: '#3a8a3a', color: 'white', border: 'none', padding: '5px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' },
  calCard: { background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflow: 'hidden', marginBottom: '20px' },
  bannerCell: { minHeight: '22px', padding: '3px', borderRight: '1px solid #f3f4f6', display: 'flex', flexDirection: 'column', gap: '2px' },
  bannerChip: { color: 'white', fontSize: '11px', fontWeight: '700', padding: '2px 6px', borderRadius: '4px', cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  weekHeaderGrid: { display: 'grid', gridTemplateColumns: '56px repeat(7, 1fr)' },
  gutterCell: { borderRight: '1px solid #f3f4f6' },
  weekBannerGrid: { display: 'grid', gridTemplateColumns: '56px repeat(7, 1fr)', borderTop: '1px solid #f3f4f6', borderBottom: '1px solid #f3f4f6' },
  gutterLabelSmall: { fontSize: '9px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', padding: '4px 6px', textAlign: 'right', borderRight: '1px solid #f3f4f6' },
  weekHourOuterGrid: { display: 'grid', gridTemplateColumns: '56px repeat(7, 1fr)' },
  hourLabelCol: { position: 'relative', borderRight: '1px solid #f3f4f6' },
  hourLabel: { position: 'absolute', right: '8px', fontSize: '10px', color: '#9ca3af', fontWeight: '600' },
  dayColToday: { background: '#f0fdf4' },
  dayHeader: { padding: '10px 8px 8px', borderBottom: '1px solid #f3f4f6', borderRight: '1px solid #f3f4f6', textAlign: 'center' },
  dayHeaderToday: { background: '#2d4a2d' },
  dayName: { display: 'block', fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' },
  dayNum: { display: 'block', fontSize: '18px', fontWeight: '700', color: '#1a2e1a', marginTop: '2px' },
  dayNumToday: { color: 'white' },
  hourGrid: { position: 'relative', borderRight: '1px solid #f3f4f6' },
  hourCell: { position: 'absolute', left: 0, right: 0, borderTop: '1px solid #f3f4f6', cursor: 'pointer' },
  jobChip: { width: '100%', textAlign: 'left', border: 'none', borderRadius: '6px', padding: '5px 8px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '2px' },
  timedChip: { position: 'absolute', border: 'none', borderRadius: '6px', padding: '3px 6px', cursor: 'grab', display: 'flex', flexDirection: 'column', gap: '1px', overflow: 'hidden', textAlign: 'left', zIndex: 2 },
  chipTitle: { fontSize: '11px', fontWeight: '700', color: '#1a2e1a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  chipTime: { fontSize: '10px', color: '#6b7c6b' },
  quickStatusBtn: { position: 'absolute', top: '2px', right: '2px', width: '16px', height: '16px', borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.7)', fontSize: '10px', lineHeight: '16px', cursor: 'pointer', padding: 0 },
  highlighted: { boxShadow: '0 0 0 3px #fbbf24' },
  conflicted: { boxShadow: '0 0 0 2px #dc2626' },
  monthGrid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' },
  monthDayLabel: { padding: '10px 8px', textAlign: 'center', fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', borderBottom: '1px solid #f3f4f6' },
  monthCell: { minHeight: '100px', padding: '6px', borderRight: '1px solid #f3f4f6', borderBottom: '1px solid #f3f4f6', cursor: 'pointer' },
  monthCellToday: { background: '#f0fdf4' },
  monthCellOther: { background: '#fafafa' },
  monthDate: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '4px' },
  monthDateToday: { background: '#2d4a2d', color: 'white', width: '22px', height: '22px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' },
  monthJobs: { display: 'flex', flexDirection: 'column', gap: '2px' },
  monthChip: { border: 'none', borderRadius: '4px', padding: '2px 6px', fontSize: '10px', fontWeight: '600', color: 'white', cursor: 'pointer', textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  moreChip: { fontSize: '10px', color: '#9ca3af', paddingLeft: '4px' },
  popupOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  popup: { background: 'white', borderRadius: '14px', padding: '24px', width: '100%', maxWidth: '460px', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  popupHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' },
  popupTitle: { margin: '0 0 4px', fontSize: '18px', fontWeight: '700', color: '#1a2e1a' },
  popupClientName: { fontSize: '13px', color: '#6b7c6b', fontWeight: '600' },
  closeBtn: { background: 'none', border: 'none', fontSize: '18px', color: '#9ca3af', cursor: 'pointer', padding: '4px' },
  conflictWarning: { background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '8px 12px', borderRadius: '8px', fontSize: '12px', marginBottom: '12px', fontWeight: '600' },
  popupBody: { display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' },
  popupRow: { display: 'flex', gap: '12px', alignItems: 'flex-start' },
  popupRow2: { display: 'flex', gap: '10px' },
  popupLabel: { display: 'block', fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' },
  popupSmallLabel: { fontSize: '12px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', minWidth: '70px', paddingTop: '1px' },
  popupInput: { width: '100%', padding: '7px 10px', borderRadius: '7px', border: '1.5px solid #d1d5db', fontSize: '13px', fontFamily: 'inherit', boxSizing: 'border-box' },
  crewCheckList: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '4px' },
  crewCheckItem: { display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', background: '#f3f4f6', padding: '5px 10px', borderRadius: '7px', cursor: 'pointer' },
  popupFooter: { display: 'flex', gap: '8px', justifyContent: 'flex-end', paddingTop: '14px', borderTop: '1px solid #f3f4f6', flexWrap: 'wrap' },
  syncBtn: { background: '#1a73e8', color: 'white', border: 'none', padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  viewJobBtn: { background: '#f3f4f6', color: '#1a2e1a', padding: '8px 16px', borderRadius: '8px', textDecoration: 'none', fontSize: '13px', fontWeight: '600' },
  saveBtn: { background: '#2d4a2d', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  unscheduledSection: { background: 'white', borderRadius: '12px', padding: '18px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  unscheduledTitle: { margin: '0 0 12px', fontSize: '15px', fontWeight: '700', color: '#92400e' },
  unscheduledList: { display: 'flex', flexDirection: 'column', gap: '6px' },
  unscheduledCard: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: '#fef9c3', borderRadius: '8px', border: '1px solid #fde68a', cursor: 'grab' },
  unscheduledName: { fontWeight: '600', color: '#1a2e1a', fontSize: '14px', flex: 1 },
  unscheduledClient: { fontSize: '13px', color: '#6b7c6b' },
  unscheduledStatus: { padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '700', textTransform: 'capitalize' },
  crewBoard: { display: 'flex', gap: '12px', overflowX: 'auto', paddingBottom: '10px' },
  crewColumn: { minWidth: '220px', background: '#f9fafb', borderRadius: '10px', padding: '10px', flexShrink: 0 },
  crewColumnHeader: { fontWeight: '700', fontSize: '13px', marginBottom: '8px', paddingTop: '4px', color: '#1a2e1a' },
  crewJobCard: { background: 'white', borderRadius: '7px', padding: '8px 10px', marginBottom: '6px', cursor: 'grab', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  crewEmpty: { fontSize: '11px', color: '#9ca3af', padding: '10px 0', textAlign: 'center' },
  agendaCard: { background: 'white', borderRadius: '10px', padding: '12px 14px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', cursor: 'pointer' },
  quickStatusBtnAgenda: { border: 'none', background: '#f3f4f6', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '700', cursor: 'pointer', textTransform: 'capitalize' },
  recurringCard: { background: 'white', borderRadius: '10px', padding: '12px 14px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', cursor: 'pointer' },
  overdueBadge: { background: '#fef2f2', color: '#dc2626', fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '10px', marginLeft: '8px' },
  skipBtn: { background: '#f3f4f6', border: 'none', padding: '5px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' },
  recurringTable: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '10px 14px', fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', borderBottom: '1px solid #f3f4f6' },
  tr: { borderBottom: '1px solid #f3f4f6' },
  td: { padding: '10px 14px', fontSize: '13px', color: '#1a2e1a', cursor: 'pointer' },
}
